import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ApiStrategiesService } from '../data/services/api/api-strategies.service';
import { ApiTemplatesService } from '../data/services/api/api-templates.service';
import { StrategyService } from '../data/services/view/strategy.service';
import { ICollectionStrategy } from '../data/interfaces/collection-strategy.interface';
import { ScenarioEnum } from '../data/enum/scenario.enum';
import { Location } from '@angular/common';
import { environment } from '@env/environment';

@Component({
  selector: 'app-strategy-detail',
  templateUrl: './strategy-detail.component.html',
  styleUrls: ['./strategy-detail.component.scss'],
  providers: [MessageService, ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StrategyDetailComponent implements OnInit {
  strategy: ICollectionStrategy | null = null;
  strategyId: string | null = null;
  isLoading = true;
  hasError = false;
  errorMessage = '';

  // Leases elegibles
  eligibleLeases: any[] = [];
  showLeasesModal = false;
  isLoadingLeases = false;
  
  // Modal de edición
  showEditModal = false;
  action = 'EDIT';

  // Enums para template
  ScenarioEnum = ScenarioEnum;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiStrategiesService: ApiStrategiesService,
    private apiTemplatesService: ApiTemplatesService,
    public strategyService: StrategyService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private location: Location,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Pre-inicializar formulario vacío para ediciones futuras (Optimización)
    this.strategyService.newEntity();
    
    // Pre-cargar templates en cache para que estén listos al abrir el wizard
    this.apiTemplatesService.getAllCached().subscribe();
    
    this.strategyId = this.route.snapshot.paramMap.get('id');
    
    if (this.strategyId) {
      this.loadStrategy(this.strategyId);
    } else {
      this.showError('ID de estrategia no válido');
    }
  }

  /**
   * Carga los datos de la estrategia desde el backend
   */
  loadStrategy(id: string): void {
    this.isLoading = true;
    this.hasError = false;
    this.cdr.markForCheck();

    this.apiStrategiesService.getCollection(id).subscribe({
      next: (response: any) => {
        if (!response.error && response.data) {
          this.strategy = response.data;
          this.hasError = false;
        } else {
          this.showError(response.msg || 'Error al cargar la estrategia');
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.showError('Error al cargar la estrategia desde el servidor');
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Muestra un error al usuario
   */
  showError(message: string): void {
    this.hasError = true;
    this.errorMessage = message;
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 5000
    });
  }

  /**
   * Retorna a la lista de estrategias
   */
  goBack(): void {
    this.location.back();
  }

  /**
   * Abre el modal para editar la estrategia
   * OPTIMIZADO: Reutiliza los datos ya cargados en this.strategy
   */
  editStrategy(): void {
    if (this.strategy) {
      this.action = 'EDIT';
      this.strategyService.loadEntityForEdit(this.strategy);
      this.showEditModal = true;
      this.cdr.markForCheck();
    } else {
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Error', 
        detail: 'No se pudieron cargar los datos de la estrategia'
      });
    }
  }

  /**
   * Obtiene el texto descriptivo del escenario
   * ACTUALIZADO: Removido CUSTOM
   */
  getScenarioLabel(scenario: ScenarioEnum): string {
    const labels: { [key in ScenarioEnum]: string } = {
      [ScenarioEnum.PRE_DUE_DATE]: 'Antes del vencimiento',
      [ScenarioEnum.DUE_DATE]: 'En la fecha de vencimiento',
      [ScenarioEnum.POST_DUE_DATE]: 'Después del vencimiento'
    };
    return labels[scenario] || scenario;
  }

  /**
   * Obtiene el icono del escenario
   */
  getScenarioIcon(scenario: ScenarioEnum): string {
    const icons: { [key in ScenarioEnum]: string } = {
      [ScenarioEnum.PRE_DUE_DATE]: 'pi-bell',
      [ScenarioEnum.DUE_DATE]: 'pi-calendar',
      [ScenarioEnum.POST_DUE_DATE]: 'pi-exclamation-triangle'
    };
    return icons[scenario] || 'pi-info-circle';
  }

  /**
   * Obtiene el color del escenario
   */
  getScenarioColor(scenario: ScenarioEnum): string {
    const colors: { [key in ScenarioEnum]: string } = {
      [ScenarioEnum.PRE_DUE_DATE]: '#3b82f6', // blue
      [ScenarioEnum.DUE_DATE]: '#f59e0b', // amber
      [ScenarioEnum.POST_DUE_DATE]: '#d21313' // red
    };
    return colors[scenario] || '#6b7280';
  }

  /**
   * Formatea el rango de días para mostrar en la UI
   * ACTUALIZADO: Usa daysRange en lugar de daysOffset
   */
  formatDaysRange(): string {
    if (!this.strategy?.daysRange) {
      return 'No configurado';
    }
    
    const { minDays, maxDays } = this.strategy.daysRange;
    
    if (this.strategy.scenario === ScenarioEnum.DUE_DATE) {
      return 'El mismo día';
    }
    
    if (maxDays === null) {
      return `${minDays}+ días`;
    }
    
    if (minDays === maxDays) {
      return `${minDays} día${minDays !== 1 ? 's' : ''}`;
    }
    
    return `${minDays} a ${maxDays} días`;
  }

  /**
   * @deprecated Usar formatDaysRange() en su lugar
   */
  formatDaysOffset(daysOffset: number): string {
    // Mantener para compatibilidad, redirigir a nuevo método
    return this.formatDaysRange();
  }

  /**
   * Formatea fechas
   */
  formatDate(date: Date | string | undefined): string {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Obtiene los canales configurados
   */
  getConfiguredChannels(): string[] {
    if (!this.strategy) return [];
    
    const channels: string[] = [];
    if (this.strategy.channels?.email?.enabled) channels.push('Email');
    if (this.strategy.channels?.sms?.enabled) channels.push('SMS');
    if (this.strategy.channels?.whatsapp?.enabled) channels.push('WhatsApp');
    if (this.strategy.channels?.call?.enabled) channels.push('Llamada');
    
    return channels;
  }

  /**
   * Obtiene el conteo de canales activos
   */
  getActiveChannelsCount(): number {
    return this.getConfiguredChannels().length;
  }

  /**
   * Verifica si un canal está habilitado
   */
  isChannelEnabled(channel: 'email' | 'sms' | 'whatsapp' | 'call'): boolean {
    return this.strategy?.channels?.[channel]?.enabled || false;
  }

  /**
   * Obtiene el nombre de la plantilla maestra de forma segura
   * masterTemplateId puede ser string o objeto populated
   */
  getMasterTemplateName(): string {
    if (!this.strategy?.masterTemplateId) return 'N/A';
    
    // Si es objeto con name, extraerlo
    if (typeof this.strategy.masterTemplateId === 'object' && 'name' in this.strategy.masterTemplateId) {
      return this.strategy.masterTemplateId.name;
    }
    
    // Si es string, mostrar ID truncado
    return String(this.strategy.masterTemplateId).slice(0, 12) + '...';
  }

  /**
   * Obtiene los leases elegibles para esta estrategia
   */
  viewEligibleLeases(): void {
    if (!this.strategyId) return;
    
    console.log('🔍 Iniciando petición de leases elegibles para estrategia:', this.strategyId);
    
    this.isLoadingLeases = true;
    this.cdr.markForCheck();
    
    this.apiStrategiesService.getEligibleLeases(this.strategyId).subscribe({
      next: (response) => {
        console.log('✅ Respuesta recibida:', response);
        
        this.isLoadingLeases = false;
        
        if (!response.error) {
          this.eligibleLeases = response.data || [];
          
          console.log('📊 Leases elegibles (raw):', this.eligibleLeases.length, this.eligibleLeases);
          
          // Mapear los datos del backend al formato esperado por el componente
          this.eligibleLeases = this.eligibleLeases.map((lease: any) => ({
            _id: lease.leaseId || lease._id,
            leaseNumber: lease.folio || lease.leaseNumber || lease.leaseId,
            folio: lease.folio,
            leaseId: lease.leaseId,
            clientId: lease.clientId,
            clientName: lease.clientName || lease.clientEmail || 'Cliente no disponible',
            clientEmail: lease.clientEmail || null,
            dueDate: lease.dueDate || lease.nextPaymentDate || null,
            pendingAmount: lease.pendingAmount || lease.totalPending || 0,
            status: lease.status || 'Activo',
            daysOverdue: lease.daysOverdue || 0,
            // Mantener campos originales por si se necesitan
            ...lease
          }));
          
          console.log('📊 Leases elegibles (mapeados):', this.eligibleLeases.length, this.eligibleLeases);
          
          if (this.eligibleLeases.length === 0) {
            console.log('⚠️ No hay leases elegibles');
            this.messageService.add({
              severity: 'info',
              summary: 'Sin Leases Elegibles',
              detail: 'No hay leases que cumplan con los criterios de esta estrategia'
            });
          } else {
            console.log('✅ Abriendo modal con', this.eligibleLeases.length, 'leases');
            this.showLeasesModal = true;
            console.log('✅ showLeasesModal =', this.showLeasesModal);
          }
        } else {
          console.error('❌ Error en respuesta:', response.msg);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.msg
          });
        }
        
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('❌ Error en petición:', error);
        this.isLoadingLeases = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al obtener los leases elegibles'
        });
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Cierra el modal de leases elegibles
   */
  hideLeasesModal(): void {
    this.showLeasesModal = false;
    this.eligibleLeases = [];
    this.cdr.markForCheck();
  }

  /**
   * Cierra el modal de edición
   */
  hideEditModal(): void {
    this.showEditModal = false;
    this.strategyService.currentStep = 1;
    this.strategyService.newEntity();
    this.cdr.markForCheck();
  }

  /**
   * Guarda los cambios de la estrategia editada
   */
  saveForm(): void {
    if (!this.strategyService.entity.valid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Incompleto',
        detail: 'Por favor complete todos los campos requeridos y valide cada paso del wizard'
      });
      return;
    }

    const data = this.strategyService.entity.value;
    
    // Transformar scheduledTime vacíos a null (backend no acepta strings vacíos)
    Object.keys(data.channels).forEach(channel => {
      if (data.channels[channel].scheduledTime === '') {
        data.channels[channel].scheduledTime = null;
      }
    });
    
    // Transformar targetAudience a estructura del backend
    if (data.targetAudience) {
      const hasData = data.targetAudience.leaseStatus?.length ||
                      data.targetAudience.daysOverdueMin !== null ||
                      data.targetAudience.daysOverdueMax !== null ||
                      data.targetAudience.amountMin !== null ||
                      data.targetAudience.amountMax !== null;
      
      if (hasData) {
        data.targetAudience = {
          status: data.targetAudience.leaseStatus || [],
          daysOverdue: {
            min: data.targetAudience.daysOverdueMin,
            max: data.targetAudience.daysOverdueMax
          },
          amountRange: {
            min: data.targetAudience.amountMin,
            max: data.targetAudience.amountMax
          }
        };
      } else {
        delete data.targetAudience;
      }
    }
    
    // Construir la ruta completa con el ID
    const path = `dripping/strategies/${this.strategyId}`;
    this.apiStrategiesService.patchCollection(data, environment.apiUrl, path).subscribe((response: any) => {
      if (response.error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error al Actualizar',
          detail: response.msg
        });
        this.cdr.markForCheck();
        return;
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Estrategia Actualizada',
        detail: 'La estrategia se actualizó correctamente'
      });

      this.hideEditModal();
      
      // Recargar la estrategia para mostrar los cambios
      if (this.strategyId) {
        this.loadStrategy(this.strategyId);
      }
    });
  }
}
